import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  linkedSignal,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { MatDialogConfig } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelect, MatSelectModule } from '@angular/material/select';

import { GridViews } from '@wcg/frontend-framework';

import { GridViewAbstract } from '../../abstracts';
import { GRID_VIEW_DROPDOWN_OPTIONS } from '../../constants';
import { GRID_FACADE_SERVICE } from '../../store';
import { GRID_LABELS, GridViewDialogData, GridViewSelect } from '../../types';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    NgClass,
    MatSelectModule,
    MatMenuModule,
  ],
  providers: [],
  selector: 'wcg-grid-view-drop-down',
  styleUrls: ['drop-down.component.scss'],
  template: `
    <form [formGroup]="dropDownForm" novalidate>
      <mat-form-field
        appearance="outline"
        class="grid-view-drop-down__form-field"
      >
        <mat-select
          [compareWith]="compareFn"
          formControlName="dropDown"
          #gridViewSelect
        >
          <div class="grid-view-drop-down__panel">
            @for (option of viewOptions(); track option.id; let last = $last) {
              <mat-option [value]="option">
                {{ option.label }}
              </mat-option>
            }
          </div>
          <footer>
            <span
              mat-menu-item
              (click)="openDialog('Edit')"
              [ngClass]="{
                'grid-view-drop-down__edit-btn-disable': $totalGridViews() <= 0,
              }"
              ><mat-icon>edit</mat-icon>
              Edit table views
            </span>
          </footer>
        </mat-select>
      </mat-form-field>
    </form>
  `,
})
export class GridViewDownComponent {
  private readonly _fb = inject(FormBuilder);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _facadeService =
    inject<GridViewAbstract>(GRID_FACADE_SERVICE);

  private readonly _gridViewMatSelect = viewChild(MatSelect);
  $totalGridViews = computed(() => this._facadeService.$gridTotal());
  $selectedView = this._facadeService.$selectedView;

  dropDownForm = this._createForm();

  viewOptions = linkedSignal({
    computation: (views) => this._mapViewOptions(views),
    source: this._facadeService.$gridViews,
  });

  constructor() {
    this._customListeners();
  }

  openDialog(event: string) {
    if (event === 'Edit') {
      this._editView();
      this._gridViewMatSelect()?.close();
    }
  }

  compareFn(option1: GridViewSelect, option2: GridViewSelect): boolean {
    return option1.id === option2.id;
  }

  private _createForm(): FormGroup<{
    dropDown: FormControl<GridViewSelect>;
  }> {
    return this._fb.group({
      dropDown: new FormControl<GridViewSelect>(GRID_VIEW_DROPDOWN_OPTIONS[0], {
        nonNullable: true,
      }),
    });
  }

  private _editView(): void {
    const config: MatDialogConfig = {
      autoFocus: false,
      width: '550px',
    };
    const _viewConfig: GridViewDialogData = {
      bulkUpdateGrids: this._facadeService.$gridViews(),
      config: config,
      gridBtnLabel: GRID_LABELS.saveChanges,
      gridHeader: GRID_LABELS.editViewTableHeader,
      gridProps: {
        grid_type: this._facadeService.$gridType(),
        name: '',
      },
      gridViewAbstract: this._facadeService,
      viewAction: GRID_LABELS.edit,
    };
    this._facadeService.openGridDialog(_viewConfig);
  }

  private _mapViewOptions(views: GridViews[]) {
    const _views = views.map((d) => ({
      id: d.id,
      label: d.name as string,
    }));
    if (_views?.length > 0) {
      return [...GRID_VIEW_DROPDOWN_OPTIONS, ..._views];
    }
    return [...GRID_VIEW_DROPDOWN_OPTIONS];
  }

  private _customListeners(): void {
    toObservable(this._facadeService.$selectedView)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (view: GridViewSelect) => {
          if (this.dropDownForm.get('dropDown')?.value.id !== view.id) {
            this.dropDownForm.get('dropDown')?.reset(view, {
              emitEvent: false,
            });
          }
        },
      });
    this.dropDownForm
      .get('dropDown')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (view: GridViewSelect) => {
          if (view?.id?.toLowerCase() === 'default') {
            this._facadeService.revertGridSettings();
          }
          this._facadeService.setSelectedView(view);
        },
      });
  }
}
